import React, { useContext } from 'react';
import { StyleSheet, TouchableHighlight, View } from 'react-native';
import DyteClient from '@dytesdk/web-core';
import { useDyteSelector } from '@dytesdk/react-native-core';
import DyteText from './DyteText';
import { SvgXml } from 'react-native-svg';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import { DyteUIContext } from '../lib/context-provider/ui-context';
import DyteSpinner from './DyteSpinner';

export default function ({
  meeting,
  size,
  variant,
  iconPack = defaultIconPack,
  t,
}: {
  meeting: DyteClient;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  variant?: 'button' | 'horizontal';
  iconPack?: IconPack;
  t?: DyteI18n;
}) {
  if (meeting.meta.viewType !== 'LIVESTREAM') return null;
  const stageStatus = useDyteSelector((m) => m.stage.status);
  const defaultLang = useLanguage();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const { storeStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  const livestreamState = useDyteSelector((m) => m.livestream.state);
  let buttonSize = 25;
  let buttonHorizontal = variant === 'horizontal' ? true : false;
  switch (size) {
    case 'md':
      buttonSize = 50;
      break;
    case 'lg':
      buttonSize = 100;
      break;
    case 'xl':
      buttonSize = 150;
      break;
  }
  switch (variant) {
    case 'horizontal':
      buttonHorizontal = true;
      break;
  }
  const styles = StyleSheet.create({
    container: {
      margin: 4,
      alignItems: 'center',
      justifyContent: 'center',
    },
    panel: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
    },
    horizontalButton: {
      padding: buttonSize / 8,
      borderRadius: 8,
      backgroundColor: colors.danger,
    },
    button: {
      height: buttonSize * 2,
      width: '100%',
      backgroundColor: colors.background[900],
    },
    text: {
      marginHorizontal: 4,
      color: colors.text,
    },
  });
  if (!meeting.self.permissions.canLivestream) return null;
  if (stageStatus !== 'ON_STAGE') return null;
  if (livestreamState === 'IDLE' || livestreamState === 'LIVESTREAMING')
    return (
      <View style={styles.container}>
        {buttonHorizontal && (
          <TouchableHighlight
            underlayColor={colors.background[800]}
            onPress={() => {
              if (livestreamState === 'IDLE') meeting.livestream.start();
              else if (livestreamState === 'LIVESTREAMING')
                meeting.livestream.stop().catch((_err) => console.log(_err));
            }}
            style={styles.horizontalButton}
          >
            <View style={styles.panel}>
              <SvgXml
                xml={(livestreamState === 'IDLE'
                  ? iconPack.start_livestream
                  : iconPack.stop_livestream
                ).replace('currentColor', colors.text)}
                width={16}
                height={16}
              />
              <DyteText size={'sm'} style={styles.text}>
                {livestreamState === 'IDLE' ? 'Go Live' : 'End'}
              </DyteText>
            </View>
          </TouchableHighlight>
        )}
        {!buttonHorizontal && (
          <TouchableHighlight
            underlayColor={colors.background[800]}
            onPress={() => {
              if (livestreamState === 'IDLE') meeting.livestream.start();
              else meeting.livestream.stop();
            }}
            style={styles.button}
          >
            <View style={styles.panel}>
              <SvgXml
                xml={(livestreamState === 'IDLE'
                  ? iconPack.start_livestream
                  : iconPack.stop_livestream
                ).replace('currentColor', colors.text)}
                width={25}
                height={25}
              />
            </View>
          </TouchableHighlight>
        )}
      </View>
    );
  else return <DyteSpinner />;
}
